import { LitElement, createRef, css, html, ref } from '@lit'
import styles from '@nova/styles'

class NovaHeader extends LitElement {
  static properties = {
    isSticky: {
      type: Boolean,
      attribute: 'is-sticky',
    },
    isTransparent: {
      type: Boolean,
      attribute: 'is-transparent',
    },
    isScrolled: {
      type: Boolean,
      reflect: true,
      attribute: 'is-scrolled',
    },
    isHovered: {
      type: Boolean,
      reflect: true,
      attribute: 'is-hovered',
    },
    isDrawerOpen: {
      type: Boolean,
      reflect: true,
      attribute: 'is-drawer-open',
    },
  }

  constructor() {
    super()
    this.isScrolled = false
    this.isHovered = false
    this.isDrawerOpen = false

    this.containerObserverRef = createRef()
  }

  connectedCallback() {
    super.connectedCallback()

    if (this.isSticky) {
      this.parentElement.classList.add('is-sticky')
    }

    window.addEventListener('scroll', this.onScrollHandler)
    this.addEventListener('mouseover', this.onHoverHandler)
    this.addEventListener('mouseout', this.offHoverHandler)
    window.addEventListener('nova:header-drawer:open', this.headerDrawerOpen)
    window.addEventListener('nova:header-drawer:close', this.headerDrawerClose)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    window.removeEventListener('scroll', this.onScrollHandler)
    this.removeEventListener('mouseover', this.onHoverHandler)
    this.removeEventListener('mouseout', this.offHoverHandler)
    window.removeEventListener('nova:header-drawer:open', this.headerDrawerOpen)
    window.removeEventListener(
      'nova:header-drawer:close',
      this.headerDrawerClose,
    )
  }

  onScrollHandler = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const body = document.querySelector('body')

    if (scrollTop >= 200) {
      if (!this.isScrolled) {
        this.isScrolled = true
        body.setAttribute('is-scrolled', true)
        this.classList.remove('reverse-animation')
      }
    } else {
      if (this.isScrolled) {
        this.isScrolled = false
        body.removeAttribute('is-scrolled')
        this.classList.add('reverse-animation')
      }
    }
  }

  onHoverHandler = () => {
    if (!this.isHovered) {
      this.isHovered = true
    }
  }

  offHoverHandler = () => {
    if (this.isHovered) {
      this.isHovered = false
    }
  }

  headerDrawerOpen = () => {
    if (!this.isDrawerOpen) {
      this.isDrawerOpen = true
    }
  }

  headerDrawerClose = () => {
    if (this.isDrawerOpen) {
      this.isDrawerOpen = false
    }
  }

  handleResize() {
    const containerElement = this.containerObserverRef.value

    if (containerElement) {
      const height = containerElement.clientHeight

      document.documentElement.style.setProperty(
        '--nova-header-height',
        `${height}px`,
      )
    }
  }

  render() {
    return html`
      <sl-resize-observer @sl-resize=${this.handleResize}>
        <div class="container" ${ref(this.containerObserverRef)}>
          <slot></slot>
        </div>
      </sl-resize-observer>
    `
  }

  static styles = [
    styles.variables,
    css`
    :host {
      display: block;
      position: relative;
      background-color: var(--nova-brand-ink);
      z-index: var(--nova-z-index-sticky);
    }

    :host([is-transparent]) {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
    }

    :host([is-sticky]) {
      position: sticky;
      top: 0;
      left: 0;
      right: 0;
      transition: all 300ms ease-in-out;
    }

    :host([is-sticky][is-scrolled]) {
      position: fixed;
      animation: slideInFromTop 300ms ease-in-out forwards;
    }

    @keyframes slideInFromTop {
      from {
        transform: translateY(-100%);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    :host([is-sticky].reverse-animation) {
      position: relative;
      animation: slideOutToTop 300ms ease-in-out forwards;
    }

    @keyframes slideOutToTop {
      from {
        transform: translateY(0);
        opacity: 1;
      }
      to {
        transform: translateY(0);
      }
    }

    :host([is-sticky][is-scrolled]),
    :host([is-sticky][is-hovered]),
    :host([is-sticky][is-drawer-open]) {
      transition: background-color 250ms;
      background-color: var(--nova-brand-ink);
    }

    .container {
      background-color: var(--nova-background, transparent);
      left: 0;
      top: 0;
      transform: translateY(0);
      transition: transform var(--nova-duration-long, 1000ms);
      width: 100%;
      z-index: 10;
      position: relative;
    }
  `,
  ]
}

customElements.define('nova-header', NovaHeader)
